import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CelestialDataLoader from '../components/starmap/CelestialDataLoader';
import HorizontalTabs from '../components/HorizontalTabs';
import TabPanel from '../components/TabPanel';
import MapPreview from '../components/create/MapPreview';
import styled from 'styled-components';

// Import step components using the index file
import {
    DesignStep,
    FormatStep,
    MomentStep,
    TextStep,
    PaymentStep
} from '../components/create/steps';

const Toast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #4CAF50;
  color: white;
  padding: 16px 32px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  opacity: ${props => props.visible ? '1' : '0'};
  transform: translateY(${props => props.visible ? '0' : '20px'});
  transition: opacity 0.3s, transform 0.3s;
`;

const CreateMapPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({
        date: '1999-12-31',
        location: '51.5074,-0.1278',
        caption: '',
        mapStyle: 'classic',
        textStyle: 'classic',
        size: '12×16 in / 30×40 cm',
        frame: 'none',
        // Display options for celestial objects
        showStars: true,
        showConstellations: true,
        showConstellationNames: true,
        showGrid: true,
        showDeepSkyObjects: true,
        showEcliptic: true,
        background: null,
        orbitalStyle: 'classic'
    });
    const [cityName, setCityName] = useState('London, UK');
    const [errors, setErrors] = useState({});
    const [mapGenerated, setMapGenerated] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [mapError, setMapError] = useState(null);
    const [showToast, setShowToast] = useState(false);
    // Add a counter to force map re-renders
    const [mapRenderKey, setMapRenderKey] = useState(0);

    // Add the missing starMapRef
    const starMapRef = useRef(null);

    // Ref to track if map should be regenerated on data change
    const shouldRegenerateMap = useRef(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Special handling for date changes to ensure smooth loading
        if (name === 'date') {
            // Start loading animation before changing date
            setIsGenerating(true);
            setLoadingProgress(0);

            // Set up a faster loading animation for date-only changes
            let progress = 0;
            const interval = setInterval(() => {
                progress += 10; // Faster increment for date-only changes
                setLoadingProgress(Math.min(progress, 95)); // Cap at 95% until fully loaded

                if (progress >= 95) {
                    clearInterval(interval);
                }
            }, 30); // Faster interval for smoother animation

            // Short delay before updating date to allow animation to start
            setTimeout(() => {
                shouldRegenerateMap.current = true;
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }, 50); // Shorter delay for date changes
        } else {
            // For non-date fields, update immediately
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleLocationSelect = (coordinates, city) => {
        setCityName(city);

        // Start the loading animation FIRST, before updating formData
        setIsGenerating(true);
        setLoadingProgress(0);

        // Set up a progressive loading animation
        let progress = 0;
        const interval = setInterval(() => {
            progress += 5; // Slower increment for smoother animation
            setLoadingProgress(Math.min(progress, 95)); // Cap at 95% until fully loaded

            if (progress >= 95) {
                clearInterval(interval);
            }
        }, 50);

        // Short delay before updating formData to allow animation to start
        setTimeout(() => {
            // Flag that we need to regenerate the map with the new location
            shouldRegenerateMap.current = true;

            // Update the formData with new location
            setFormData(prev => ({
                ...prev,
                location: coordinates
            }));

            // Force component remounting by incrementing the key
            setMapRenderKey(prevKey => prevKey + 1);
        }, 100);
    };

    const handleOptionSelect = (optionType, optionId) => {
        setFormData(prev => ({
            ...prev,
            [optionType]: optionId
        }));
    };

    const handleToggleCelestialElement = (elementName) => {
        // Update the formData state
        setFormData(prev => {
            const newState = {
                ...prev,
                [elementName]: !prev[elementName]
            };

            // Update Celestial settings if available
            if (window.Celestial && typeof window.Celestial.apply === 'function') {
                try {
                    // Create the proper update structure based on what's being toggled
                    const updateConfig = {};

                    // Set the correct path based on the element being toggled
                    switch (elementName) {
                        case 'showStars':
                            updateConfig.stars = { show: !prev[elementName] };
                            break;
                        case 'showConstellations':
                            updateConfig.constellations = {
                                lines: !prev[elementName]
                            };
                            break;
                        case 'showConstellationNames':
                            updateConfig.constellations = {
                                names: !prev[elementName]
                            };
                            break;
                        case 'showGrid':
                            updateConfig.lines = {
                                graticule: { show: !prev[elementName] },
                                equatorial: { show: !prev[elementName] }
                            };
                            break;
                        case 'showDeepSkyObjects':
                            updateConfig.dsos = { show: !prev[elementName] };
                            break;
                        case 'showEcliptic':
                            updateConfig.lines = {
                                ecliptic: { show: !prev[elementName] }
                            };
                            break;
                        default:
                            break;
                    }

                    // Apply the changes
                    window.Celestial.apply(updateConfig);

                    // Redraw without reloading data
                    window.Celestial.redraw();
                } catch (error) {
                    console.error('Error updating Celestial settings:', error);
                }
            }

            return newState;
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.date) newErrors.date = 'Date is required';
        if (!formData.location) newErrors.location = 'Location is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleGenerateMap = () => {
        if (validateForm()) {
            setIsGenerating(true);
            setLoadingProgress(0);
            shouldRegenerateMap.current = true;

            // Use a smoother loading animation
            let progress = 0;
            const interval = setInterval(() => {
                progress += 5; // Slower increment for smoother animation
                setLoadingProgress(Math.min(progress, 95)); // Cap at 95% until fully loaded

                if (progress >= 95) {
                    clearInterval(interval);
                }
            }, 50);

            // Map generation will complete in StarMapGenerator component
            // The handleStarMapLoaded callback will finish the progress and hide the loader
        }
    };

    const handleAddToCart = () => {
        if (validateForm() && mapGenerated) {
            setIsAddingToCart(true);

            // Capture the current star map image if possible
            let starMapImageData = null;
            if (starMapRef && starMapRef.current) {
                try {
                    starMapImageData = starMapRef.current.captureStarMap();
                } catch (error) {
                    console.error("Failed to capture star map image:", error);
                }
            }

            // Create star map object for cart
            const starMap = {
                id: 'map-' + Date.now(), // Simple unique ID
                type: 'star_map',
                name: 'Custom Star Map',
                date: formData.date,
                location: cityName, // Use the display city name instead of coordinates
                caption: formData.caption,
                style: formData.mapStyle,
                size: formData.size,
                frame: formData.frame,
                background: formData.background,
                // Include celestial elements
                showStars: formData.showStars,
                showConstellations: formData.showConstellations,
                showConstellationNames: formData.showConstellationNames,
                showGrid: formData.showGrid,
                showDeepSkyObjects: formData.showDeepSkyObjects,
                showEcliptic: formData.showEcliptic,
                price: calculatePrice(),
                quantity: 1,
                imageUrl: '/images/star-map-preview.jpg', // Replace with actual preview image if available
                starMapImageData // Store the image data for later use
            };

            // Get existing cart from localStorage or initialize empty array
            let cart = JSON.parse(localStorage.getItem('cart') || '[]');

            // Add new item to cart
            cart.push(starMap);

            // Save updated cart to localStorage
            localStorage.setItem('cart', JSON.stringify(cart));

            // Dispatch custom event to notify components of cart change
            window.dispatchEvent(new Event('cartUpdated'));

            // Show toast and navigate after a delay
            setShowToast(true);

            setTimeout(() => {
                setIsAddingToCart(false);
                setShowToast(false);
                navigate('/cart');
            }, 1000);
        }
    };

    const calculatePrice = () => {
        // Base price
        let price = 29.99;

        // Add price based on size
        const sizePrices = {
            '8×10 in / 20×25 cm': 0.00,  // Base price includes small size
            '12×16 in / 30×40 cm': 10.00,
            '24×36 in / 60×90 cm': 20.00
        };
        price += sizePrices[formData.size] || 10.00; // Default to medium size price if not found

        // Add price based on frame
        if (formData.frame === 'wood') price += 15.00;
        else if (formData.frame === 'metal') price += 25.00;
        else if (formData.frame === 'black') price += 20.00;

        // Add price based on background
        if (formData.background) {
            // Map background options to their prices
            const backgroundPrices = {
                'basic-light': 5.00,
                'basic-dark': 5.00,
                'sunset-pink': 8.00,
                'sunset-orange': 8.00,
                'pastel-pink': 6.50,
                'pastel-blue': 6.50,
                'pastel-white': 6.50,
                'neon-purple': 9.00,
                'neon-blue': 9.00,
                'natural-gold': 7.00,
                'natural-brown': 7.00,
                'natural-beige': 7.00,
                'golden-dark': 11.00,
                'golden-black': 11.00,
                'golden-gold': 11.00
            };

            price += backgroundPrices[formData.background] || 0;
        }

        // Add price based on map style
        if (formData.mapStyle) {
            // Map style options to their prices
            const mapStylePrices = {
                'classic': 0.00,  // Base price already includes classic style
                'minimal': 0.00,
                'vintage': 15.00,
                'cosmic': 15.00,
                'modern': 15.00,
                'nebula': 25.00,
                'midnight': 25.00,
                'constellation': 25.00
            };

            price += mapStylePrices[formData.mapStyle] || 0;
        }

        // Add price based on orbital style
        if (formData.orbitalStyle) {
            // Map orbital style options to their prices
            const orbitalStylePrices = {
                'classic': 0.00,
                'minimal': 0.00,
                'geometric': 2.50,
                'celestial': 3.50,
                'cosmic': 4.00,
                'spiral': 3.00,
                'wave': 3.50,
                'orbit': 4.50,
                'heart': 5.50,
                'starburst': 5.50,
                'asymmetric': 5.00,
                'star': 6.00,
                'galaxy': 6.50
            };

            price += orbitalStylePrices[formData.orbitalStyle] || 0;
        }

        // Add price based on celestial elements
        const celestialElementPrices = {
            'showStars': 4.00,
            'showConstellations': 5.00,
            'showConstellationNames': 5.00,
            'showGrid': 8.00,
            'showDeepSkyObjects': 13.00,
            'showEcliptic': 17.50
        };

        // Add the prices for enabled celestial elements
        Object.keys(celestialElementPrices).forEach(element => {
            if (formData[element]) {
                price += celestialElementPrices[element];
            }
        });

        return price.toFixed(2);
    };

    const handleStarMapError = (error) => {
        console.error("Star map generation error:", error);
        setMapError(error);
        setIsGenerating(false);
    };

    const handleStarMapLoaded = () => {
        // When the star map is fully loaded and animation is complete
        console.log("Star map fully loaded");
        setMapGenerated(true);

        // Complete the loading progress to 100%
        setLoadingProgress(100);

        // The loading indicator will now stay visible until the animation completes
        // as controlled by the StarMapGenerator component's notifyMapLoaded method

        shouldRegenerateMap.current = false;
    };

    // Handle tab change
    const handleTabChange = (newValue) => {
        setActiveTab(newValue);
        // Only regenerate map if it's the first time viewing this tab
        if (!mapGenerated) {
            handleGenerateMap();
        }
    };

    // Generate map on initial load
    useEffect(() => {
        // Start with map generation on component mount
        setIsGenerating(true);
        setLoadingProgress(0);
        shouldRegenerateMap.current = true;

        // Use a smoother loading animation
        let progress = 0;
        const interval = setInterval(() => {
            progress += 5; // Slower increment for smoother animation
            setLoadingProgress(Math.min(progress, 95)); // Cap at 95% until fully loaded

            if (progress >= 95) {
                clearInterval(interval);
            }
        }, 50);

        // The handleStarMapLoaded callback will finish the progress and hide the loader

        return () => clearInterval(interval);
    }, []);

    // Listen for animation completion
    useEffect(() => {
        const handleAnimationComplete = () => {
            console.log("Star map animation complete");
            // Hide the loading indicator
            setIsGenerating(false);
        };

        // Add event listener
        window.addEventListener('starmap-animation-complete', handleAnimationComplete);

        // Clean up
        return () => {
            window.removeEventListener('starmap-animation-complete', handleAnimationComplete);
        };
    }, []);

    // Define tabs
    const tabs = [
        { label: 'Design', component: DesignStep },
        { label: 'Format', component: FormatStep },
        { label: 'Moment', component: MomentStep },
        { label: 'Text', component: TextStep },
        { label: 'Payment', component: PaymentStep }
    ];

    return (
        <div className="relative">
            {/* Data Loader for d3-celestial */}
            <CelestialDataLoader
                onProgressUpdate={(progress) => console.log("Data loading progress:", progress)}
            />

            <div className="relative z-1 -mt-6">
                <div className="flex flex-col items-center justify-start w-full !bg-cover !bg-center pt-[14vw]" style={{
                    backgroundImage: "url('/images/header-small.webp')",
                    backgroundSize: "cover",
                    backgroundPosition: "center"
                }}>
                    <h1 className="text-[30px] md:text-[55px] font-light text-white text-center mb-12">Your Stars, Your Story</h1>
                </div>

                <div className="px-8 md:px-16 relative z-10 max-w-[1480px] mx-auto">
                    {/* Price Display */}
                    <div className="flex flex-col md:flex-row justify-between md:items-center mb-4 pb-4 border-b border-white/10 hidden">
                        <div className="mb-2 md:mb-0 text-center md:text-left">
                            <h2 className="text-lg md:text-xl font-bold text-white">Your Custom Star Map</h2>
                            <p className="text-xs md:text-sm text-gray-400">Customize every detail to make it uniquely yours</p>
                        </div>
                        <div className="flex flex-col items-center md:items-end">
                            <div className="text-xl md:text-2xl font-bold text-accent">${calculatePrice()}</div>
                            <div className="text-xs text-gray-400">Free shipping included</div>
                        </div>
                    </div>

                    {/* Horizontal Tabs */}
                    <HorizontalTabs
                        tabs={tabs}
                        activeTab={activeTab}
                        onChange={handleTabChange}
                    />

                    <div className="grid !grid-cols-1 md:!grid-cols-1 lg:!grid-cols-4 gap-4 md:gap-6 w-full mt-10 md:mt-16">
                        {/* Preview Section - Moved to left side */}
                        {activeTab !== 0 && (
                            <div className="col-span-1 md:col-span-2 order-1 lg:order-2">
                                <MapPreview
                                    formData={formData}
                                    isGenerating={isGenerating}
                                    loadingProgress={loadingProgress}
                                    mapGenerated={mapGenerated}
                                    mapError={mapError}
                                    handleStarMapError={handleStarMapError}
                                    handleStarMapLoaded={handleStarMapLoaded}
                                    shouldRegenerateMap={shouldRegenerateMap}
                                    starMapRef={starMapRef}
                                    key={mapRenderKey}
                                />
                            </div>
                        )}

                        {/* Form Inputs - Moved to right side */}
                        <div className={`${activeTab === 0 ? 'lg:col-span-5' : 'lg:col-span-2'} space-y-4 md:space-y-6 order-1 lg:order-2`}>

                            {/* Design Tab */}
                            <TabPanel value={activeTab} index={0}>
                                <DesignStep
                                    formData={formData}
                                    handleOptionSelect={handleOptionSelect}
                                />
                            </TabPanel>

                            {/* Format Tab */}
                            <TabPanel value={activeTab} index={1}>
                                <FormatStep
                                    formData={formData}
                                    handleOptionSelect={handleOptionSelect}
                                />
                            </TabPanel>

                            {/* Moment Tab */}
                            <TabPanel value={activeTab} index={2}>
                                <MomentStep
                                    formData={formData}
                                    handleInputChange={handleInputChange}
                                    handleLocationSelect={handleLocationSelect}
                                    handleToggleCelestialElement={handleToggleCelestialElement}
                                    cityName={cityName}
                                    errors={errors}
                                />
                            </TabPanel>

                            {/* Text Tab */}
                            <TabPanel value={activeTab} index={3}>
                                <TextStep
                                    formData={formData}
                                    handleInputChange={handleInputChange}
                                />
                            </TabPanel>

                            {/* Payment Tab */}
                            <TabPanel value={activeTab} index={4}>
                                <PaymentStep
                                    formData={formData}
                                    calculatePrice={calculatePrice}
                                    isAddingToCart={isAddingToCart}
                                    handleAddToCart={handleAddToCart}
                                    mapGenerated={mapGenerated}
                                />
                            </TabPanel>

                            {/* Navigation buttons */}
                            <div className="flex flex-row gap-4 justify-center items-center mt-6 sm:mt-8">
                                {activeTab > 0 && activeTab !== 4 && (
                                    <button
                                        onClick={() => setActiveTab(prev => prev - 1)}
                                        className="flex items-center justify-center mx-auto w-auto py-2.5 sm:py-3 px-4 sm:px-6 rounded-full font-light transition duration-300 bg-white text-primary hover:bg-gray-100"
                                    >
                                        <span className="text-[14px] md:text-md">Back</span>
                                    </button>
                                )}

                                {activeTab !== 4 && activeTab !== 0 && (
                                    <button
                                        onClick={handleGenerateMap}
                                        disabled={isGenerating}
                                        className="flex items-center justify-center mx-auto w-[300px] py-2.5 sm:py-3 px-4 sm:px-6 rounded-full font-light transition duration-300 bg-white text-primary hover:bg-gray-100"
                                    >
                                        {isGenerating ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 sm:h-5 sm:w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span className="text-[14px] md:text-md text-nowrap">Generating...</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                <span className="text-[14px] md:text-md text-nowrap">Refresh Preview</span>
                                            </>
                                        )}
                                    </button>
                                )}

                                {activeTab < 4 && (
                                    <button
                                        onClick={() => setActiveTab(prev => prev + 1)}
                                        className="flex items-center justify-center mx-auto w-[300px] py-2.5 sm:py-3 px-4 sm:px-6 rounded-full font-light transition duration-300 bg-white text-primary hover:bg-gray-100"
                                    >
                                        <span className="text-[14px] md:text-md">Next Step</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="min-h-[120px]"></div>

            <Toast visible={showToast}>
                Added to cart!
            </Toast>
        </div>
    );
};

export default CreateMapPage; 